<template>
    <svg
        v-if="drawn"
        :class="{ drawing: 'drawing' }"
        @mousemove="mouseMove"
        @mouseup="mouseUp"
    >
        <rect
            :x="x"
            :y="y"
            :width="width"
            :height="height"
            class="box"
            @mousedown.stop="startMove"
        />

        <g v-if="!drawing">
            <rect
                v-for="edge in edges"
                :key="edge.class"
                :class="edge.class"
                :x="edge.x"
                :y="edge.y"
                width="10"
                height="10"
                @mousedown.stop="startResize"
                @dragstart.prevent=""
            />
        </g>
    </svg>
</template>

<script>
export default {
    name: 'Cropbox',
    props: {
        drawn: {
            type: Boolean,
            required: true
        },
        drawing: {
            type: Boolean,
            required: true
        },
        x: {
            type: Number,
            required: true
        },
        y: {
            type: Number,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        width: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            moving: false,
            resizing: false,
            resizeDirection: [],
            posDim: {
                x: 0,
                y: 0,
                height: 0,
                width: 0
            }
        };
    },
    computed: {
        edges() {
            const left = this.x - 10;
            const right = this.x + this.width;
            const top = this.y - 10;
            const bottom = this.y + this.height;
            const xCentre = this.x + (this.width / 2) - 5;
            const yCentre = this.y + (this.height / 2) - 5;

            return [
                {
                    class: 'nw',
                    x: left,
                    y: top
                },
                {
                    class: 'n',
                    x: xCentre,
                    y: top
                },
                {
                    class: 'ne',
                    x: right,
                    y: top
                },
                {
                    class: 'w',
                    x: left,
                    y: yCentre
                },
                {
                    class: 'e',
                    x: right,
                    y: yCentre
                },
                {
                    class: 'sw',
                    x: left,
                    y: bottom
                },
                {
                    class: 's',
                    x: xCentre,
                    y: bottom
                },
                {
                    class: 'se',
                    x: right,
                    y: bottom
                }
            ];
        }
    },
    methods: {
        mouseMove(e) {
            if (this.drawing) {
                return e;
            }
            if (this.moving) {
                this.move(e);
            }
            if (this.resizing) {
                this.resize(e);
            }
            return null;
        },
        mouseUp() {
            if (this.resizing) {
                this.resizing = false;
            }
            if (this.moving) {
                this.moving = false;
            }
        },
        cloneState(e) {
            this.posDim.x = this.x;
            this.posDim.y = this.y;
            this.posDim.height = this.height;
            this.posDim.width = this.width;
            this.mx = e.layerX || e.offsetX;
            this.my = e.layerY || e.offsetY;
        },
        startResize(e) {
            if (this.drawing) return;

            this.resizing = true;
            this.resizeDirection = e.target.getAttribute('class')
                .split('');
            this.cloneState(e);
        },
        resize(e) {
            e.preventDefault();

            const x = e.layerX || e.offsetX;
            const y = e.layerY || e.offsetY;

            const xDiff = x - this.mx;
            const yDiff = y - this.my;

            const dimensions = {
                height: this.posDim.height,
                width: this.posDim.width,
                x: this.posDim.x,
                y: this.posDim.y
            };

            if (this.resizeDirection.includes('w')) {
                dimensions.width -= xDiff;
                dimensions.x += xDiff;
            }
            else if (this.resizeDirection.includes('e')) {
                dimensions.width += xDiff;
            }

            if (this.resizeDirection.includes('n')) {
                dimensions.height -= yDiff;
                dimensions.y += yDiff;
            }
            else if (this.resizeDirection.includes('s')) {
                dimensions.height += yDiff;
            }

            this.$emit('setDimensions', dimensions);
        },
        startMove(e) {
            if (this.drawing) return;
            this.moving = true;
            this.cloneState(e);
        },
        move(e) {
            e.preventDefault();

            const x = e.layerX || e.offsetX;
            const y = e.layerY || e.offsetY;

            const xDiff = x - this.mx;
            const yDiff = y - this.my;

            const dimensions = {
                height: this.posDim.height,
                width: this.posDim.width,
                x: this.posDim.x + xDiff,
                y: this.posDim.y + yDiff
            };
            this.$emit('setDimensions', dimensions);
        }
    }
};
</script>

<style lang="scss" scoped>
    svg {
        shape-rendering: geometricPrecision;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;

        &.drawing {
            pointer-events: all;
        }

        .box {
            stroke: rgba(200, 200, 200, .7);
            stroke-width: 2;
            stroke-dasharray: 8 8;
            fill: none;
            cursor: move;
        }

        g {
            fill: #222222;
            stroke-width: 2;
            stroke: #cccccc;
        }

        .nw {
            cursor: nw-resize;
        }

        .n {
            cursor: n-resize;
        }

        .ne {
            cursor: ne-resize;
        }

        .w {
            cursor: w-resize;
        }

        .e {
            cursor: e-resize;
        }

        .sw {
            cursor: sw-resize;
        }

        .s {
            cursor: s-resize;
        }

        .se {
            cursor: se-resize;
        }
    }
</style>
